import { ApolloClient } from "@apollo/client";
import { GetPaymentBatchesConnectionForExportDocument } from "#graphql/supergraphHooks";
import {
  type GetPaymentBatchesConnectionForExportQuery,
  type PaymentBatchesFilterInput,
} from "#graphql/supergraphTypes";
import { CsvColumn, downloadCsv, toCsv } from "#utils/csv";
import { clientContext } from "#utils/graphql_utils";
import { paymentBatchGQLFactory } from "./Factories/paymentBatchGQLFactory";
import { PaymentExportRow } from "./types";
import { preparePaginationQuery } from "./util/tablePaginationUtils";

/**
 * Number of batches requested per GraphQL roundtrip while looping the export.
 * Expected dataset size is <=100 batches per tenant, so 200 comfortably fits
 * the whole export in a single roundtrip while leaving headroom for growth.
 */
export const EXPORT_PAGE_SIZE = 200;

/**
 * Hard cap on pages to prevent unbounded looping if the server falsely reports
 * `hasNextPage: true`. With {@link EXPORT_PAGE_SIZE}=200 this allows up to 2k
 * batches per export, which is well above expected dataset sizes.
 */
export const EXPORT_MAX_PAGES = 10;

/**
 * Column definitions written to the export CSV, in the order they appear.
 * Headers are user-facing labels; `value` resolvers pull from {@link PaymentExportRow}.
 *
 * The Client column is omitted for GPx exports because a GPx user is always
 * scoped to a single client and the column is hidden in their table view as well
 * (see `getColumnDefinitions` in `columnDefinitionFactory.tsx`).
 */
export const buildExportColumns = (isGPX: boolean): readonly CsvColumn<PaymentExportRow>[] => {
  const columns: CsvColumn<PaymentExportRow>[] = [
    { header: "Entity", value: (r) => r.entityName },
    { header: "Payment Type", value: (r) => r.paymentType },
    { header: "Payee", value: (r) => r.payee },
    { header: "Batch Status", value: (r) => r.batchStatus },
    { header: "Payment Status", value: (r) => r.paymentStatus },
    { header: "Amount", value: (r) => r.paymentAmount },
    { header: "Currency", value: (r) => r.paymentCurrency },
    { header: "Description", value: (r) => r.paymentDescription },
    { header: "Payment Date", value: (r) => r.paymentDate },
    { header: "Payment Method", value: (r) => r.paymentMethod },
    { header: "Investor Account", value: (r) => r.investorAccountName },
    { header: "Position ID", value: (r) => r.positionId },
    { header: "Payment ID", value: (r) => r.paymentId },
  ];
  if (!isGPX) {
    columns.unshift({ header: "Client", value: (r) => r.clientName });
  }
  return columns;
};

/**
 * Build the YYYY-MM-DD filename component using the local date.
 */
export const buildExportFilename = (now: Date = new Date()): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `payments-${y}-${m}-${d}.csv`;
};

/**
 * Loop the GetPaymentBatchesConnectionForExport query, accumulating flattened
 * per-payment rows. Honors the provided filter (which mirrors the table filter)
 * and stops when the server reports no further pages or the safety cap is hit.
 */
export const fetchPaymentBatchesForExport = async ({
  client,
  inputFilter,
}: {
  client: ApolloClient<object>;
  inputFilter: PaymentBatchesFilterInput;
}): Promise<PaymentExportRow[]> => {
  const rows: PaymentExportRow[] = [];
  let startRow = 0;
  for (let page = 0; page < EXPORT_MAX_PAGES; page++) {
    const paginationArgs = preparePaginationQuery(startRow, EXPORT_PAGE_SIZE);
    const response = await client.query<GetPaymentBatchesConnectionForExportQuery>({
      query: GetPaymentBatchesConnectionForExportDocument,
      context: clientContext(),
      variables: { inputFilter, paginationArgs },
      fetchPolicy: "no-cache",
    });
    rows.push(...paymentBatchGQLFactory.toExportRows(response.data));
    if (!response.data.paymentBatchesConnection.pageInfo?.hasNextPage) {
      break;
    }
    startRow += EXPORT_PAGE_SIZE;
  }
  return rows;
};

/**
 * End-to-end export entry point: fetch all matching batches, serialize them to
 * CSV, and trigger a browser download.
 *
 * @returns The number of payment rows written to the CSV (i.e. the row count
 * excluding the header line). Useful for telemetry and tests.
 */
export const exportPaymentBatchesToCsv = async ({
  client,
  inputFilter,
  isGPX,
  filename = buildExportFilename(),
}: {
  client: ApolloClient<object>;
  inputFilter: PaymentBatchesFilterInput;
  isGPX: boolean;
  filename?: string;
}): Promise<number> => {
  const rows = await fetchPaymentBatchesForExport({ client, inputFilter });
  const csv = toCsv(rows, buildExportColumns(isGPX));
  downloadCsv(csv, filename);
  return rows.length;
};
